/**
 * Unit Measurement actions
 */
import apiRequest from 'store/api/actions';
import { doGet, doPatch, doPut } from 'system/httpHelper';
import { endpointFormatter } from 'utils/endpoint';
import {
  FETCH_NOTIFICATIONS_SETTINGS_FAIL,
  FETCH_NOTIFICATIONS_SETTINGS_START,
  SET_NOTIFICATION_PREFERENCES,
  SET_NOTIFICATIONS_SETTINGS,
  SET_NOTIFICATION_PREFERENCES_FAIL,
  SET_NOTIFICATION_PREFERENCES_START,
  SET_UNIT_MEASUREMENT,
  SET_UNIT_MEASUREMENT_FAIL,
  SET_UNIT_MEASUREMENT_START,
  FETCH_SITE_NOTIFICATIONS_START,
  SET_SITE_NOTIFICATIONS,
  FETCH_SITE_NOTIFICATIONS_FAIL,
  PATCH_SITE_NOTIFICATIONS,
  PATCH_SITE_NOTIFICATIONS_FAIL,
  SET_DARK_MODE_START,
  SET_DARK_MODE,
  SET_DARK_MODE_FAIL,
  SET_ALARM_PREFERENCES_START,
  SET_ALARM_PREFERENCES,
  SET_ALARM_PREFERENCES_BATCH,
  REMOVE_ALARM_PREFERENCE_SITE,
  SET_ALARM_PREFERENCES_FAIL,
} from './types';

const normalizeAlarmPreferencesResponse = (response) => {
  let payload = response;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (error) {
      return [];
    }
  }

  let preferences = [];
  if (Array.isArray(payload)) {
    preferences = payload;
  } else if (Array.isArray(payload?.alarmPreferences)) {
    preferences = payload.alarmPreferences;
  } else if (Array.isArray(payload?.preferences)) {
    preferences = payload.preferences;
  } else if (payload && typeof payload === 'object') {
    preferences = [payload];
  }

  const normalizeList = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((entry) => (entry === null || entry === undefined ? '' : `${entry}`.trim()))
        .filter(Boolean);
    }
    if (value === null || value === undefined || value === '') {
      return [];
    }
    return [`${value}`.trim()].filter(Boolean);
  };

  return preferences
    .map((preference) => {
      if (!preference) {
        return null;
      }
      const siteId =
        preference.siteId ?? preference.site_id ?? preference.siteID ?? preference.site;
      if (!siteId) {
        return null;
      }
      return {
        siteId,
        subscribedCategories: normalizeList(
          preference.subscribedCategories ||
            preference.subscribed_categories ||
            preference.categories,
        ),
        subscribedDevices: normalizeList(
          preference.subscribedDevices || preference.subscribed_devices || preference.devices,
        ),
        enableSMS:
          preference.enableSMS ??
          preference.enableSms ??
          preference.enable_sms ??
          preference.smsEnabled ??
          preference.sms_enabled,
        enableEmail:
          preference.enableEmail ??
          preference.enable_email ??
          preference.emailEnabled ??
          preference.email_enabled,
        enableBellNotification:
          preference.enableBellNotification ??
          preference.enable_bell_notification ??
          preference.bellEnabled ??
          preference.bell_enabled,
      };
    })
    .filter(Boolean);
};

/**
 * setUnitMeasurementStart
 * Start the set unit measurement
 */
export const setUnitMeasurementStart = () => ({
  type: SET_UNIT_MEASUREMENT_START,
});

/**
 * setUnitMeasurement
 * Save the new unit measurement
 * @param {Object} unit receives new unit measurement data
 */
export const setUnitMeasurement = (unit) => ({
  type: SET_UNIT_MEASUREMENT,
  payload: unit,
});

/**
 * setUnitMeasurementFail
 * Save the error fail when the new unit measurement
 * @param {Object} error receives error from set
 */
export const setUnitMeasurementFail = (error) => ({
  type: SET_UNIT_MEASUREMENT_FAIL,
  payload: error,
});

/**
 * fetchNotificationsSettingsStart
 * Start the notifications settings
 */
export const fetchNotificationsSettingsStart = () => ({
  type: FETCH_NOTIFICATIONS_SETTINGS_START,
});

/**
 * setNotificationsSettings
 * Save the new notifications settings
 * @param {Object} notifications receives new notification settings data
 */
export const setNotificationsSettings = (notifications) => ({
  type: SET_NOTIFICATIONS_SETTINGS,
  payload: notifications,
});

export const setDarkModeStart = () => ({
  type: SET_DARK_MODE_START,
});

export const setDarkMode = (darkMode) => ({
  type: SET_DARK_MODE,
  payload: darkMode,
});

export const setDarkModeFail = (error) => ({
  type: SET_DARK_MODE_FAIL,
  payload: error,
});

export const setAlarmPreferencesStart = () => ({
  type: SET_ALARM_PREFERENCES_START,
});

export const setAlarmPreferences = (preferences) => ({
  type: SET_ALARM_PREFERENCES,
  payload: preferences,
});

export const setAlarmPreferencesBatch = (preferencesBySite) => ({
  type: SET_ALARM_PREFERENCES_BATCH,
  payload: preferencesBySite,
});

export const removeAlarmPreferenceSite = (siteId) => ({
  type: REMOVE_ALARM_PREFERENCE_SITE,
  payload: siteId,
});

export const setAlarmPreferencesFail = (error) => ({
  type: SET_ALARM_PREFERENCES_FAIL,
  payload: error,
});

/**
 * fetchNotificationsSettingsFail
 * Save the error fail when fetch the notification settings
 * @param {Object} error receives error from fetch
 */
export const fetchNotificationsSettingsFail = (error) => ({
  type: FETCH_NOTIFICATIONS_SETTINGS_FAIL,
  payload: error,
});

/**
 * setNotificationStart
 * Start the set notification
 */
export const setNotificationStart = () => ({
  type: SET_NOTIFICATION_PREFERENCES_START,
});

/**
 * setNotification
 * Save the new notifications
 * @param {Object} notifications receives new notifications data
 */
export const setNotification = (notifications) => ({
  type: SET_NOTIFICATION_PREFERENCES,
  payload: notifications,
});

/**
 * setNotificationFail
 * Save the error fail when set notifications
 * @param {*} error receives error from set
 */
export const setNotificationFail = (error) => ({
  type: SET_NOTIFICATION_PREFERENCES_FAIL,
  payload: error,
});

/**
 * fetchSiteNotificationsStart
 * Start the sites
 */
export const fetchSiteNotificationsStart = () => ({
  type: FETCH_SITE_NOTIFICATIONS_START,
});

/**
 * setSiteNotifications
 * Save the sites
 * @param {Object} sites receives new sites data
 */
export const setSiteNotifications = (sites) => ({
  type: SET_SITE_NOTIFICATIONS,
  payload: sites,
});

/**
 * fetchNotificationsFail
 * Save the error fail when fetch the sites
 * @param {Object} error receives error from fetch
 */
export const fetchNotificationsFail = (error) => ({
  type: FETCH_SITE_NOTIFICATIONS_FAIL,
  payload: error,
});

/**
 * getAllNotificationsSettings
 * Find all notifications for user settings
 * @return Promise
 */
export const getAllNotificationsSettings = () => (dispatch) =>
  new Promise(() => {
    dispatch(fetchNotificationsSettingsStart());
    const apiUrl = `${endpointFormatter('fetchNotificationsSettings')}`;
    const onSuccess = (response) => {
      let data = response;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch (error) {
          return fetchNotificationsSettingsFail(error);
        }
      }
      return setNotificationsSettings(data);
    };

    dispatch(apiRequest(() => doGet(apiUrl), onSuccess, setNotificationFail));
  });

export const updateUserDarkMode = (darkMode) => (dispatch) => {
  dispatch(setDarkModeStart());
  const apiUrl = `${endpointFormatter('updateUserDarkModeSettings')}`;
  const requestData = {
    darkMode: darkMode === true,
  };

  const onSuccess = () => setDarkMode(darkMode === true);

  dispatch(apiRequest(() => doPut(apiUrl, requestData), onSuccess, setDarkModeFail, true, false));
};

/**
 * getSiteNotifications
 * Find all sites for notifications
 * @return Promise
 */
export const getSiteNotifications = () => (dispatch) =>
  new Promise(() => {
    dispatch(fetchSiteNotificationsStart());
    const apiUrl = `${endpointFormatter('fetchSiteNotifications')}`;

    dispatch(apiRequest(() => doGet(apiUrl), setSiteNotifications, fetchNotificationsFail));
  });

/**
 * patchSiteNotifications
 * Save updated site
 * @param {Object} site
 */
export const patchSiteNotifications = (site) => ({
  type: PATCH_SITE_NOTIFICATIONS,
  payload: site,
});

/**
 * patchSiteNotificationsFail
 * Save the error fail when fetch the sites
 * @param {Object} error receives error from fetch
 */
export const patchSiteNotificationsFail = (error) => ({
  type: PATCH_SITE_NOTIFICATIONS_FAIL,
  payload: error,
});

/**
 * updateSiteNotificationSettings
 * Update notifications settings from toggle button
 * @param {Number} settingId receives setting ID from settings user
 * @param {Object} notificationsData receives data to modify
 */
export const updateSiteNotificationSettings = (site) => (dispatch) => {
  dispatch(fetchSiteNotificationsStart());
  const requestData = [site];
  const apiUrl = `${endpointFormatter('fetchSiteNotifications')}`;

  dispatch(
    apiRequest(
      () => doPatch(apiUrl, requestData),
      patchSiteNotifications,
      patchSiteNotificationsFail,
      true,
      false,
    ),
  );
};

/**
 * updateAlarmNotificationPreferences
 * Save category/device alarm preferences for a specific site.
 * These preferences are used by backend workflows for both SMS and email delivery.
 * @param {Object} preferenceData
 */
export const updateAlarmNotificationPreferences = (preferenceData) => (dispatch) => {
  dispatch(setAlarmPreferencesStart());
  const apiUrl = `${endpointFormatter('updateAlarmNotificationPreferences')}`;
  const requestData = {
    siteId: preferenceData.siteId,
    subscribedCategories: preferenceData.subscribedCategories,
    subscribedDevices: preferenceData.subscribedDevices,
    enableSMS: preferenceData.enableSMS,
    enableEmail: preferenceData.enableEmail,
    enableBellNotification: preferenceData.enableBellNotification,
  };

  const onSuccess = () => setAlarmPreferences(requestData);

  dispatch(
    apiRequest(() => doPut(apiUrl, requestData), onSuccess, setAlarmPreferencesFail, true, false),
  );
};

/**
 * updateAlarmNotificationPreferencesBatch
 * Save category/device alarm preferences for one or many sites.
 * @param {Array} preferencesBySite array of
 * { siteId, subscribedCategories, subscribedDevices, enableSMS, enableEmail }
 */
export const updateAlarmNotificationPreferencesBatch = (preferencesBySite) => (dispatch) => {
  dispatch(setAlarmPreferencesStart());
  const apiUrl = `${endpointFormatter('updateAlarmNotificationPreferences')}`;
  const fetchUrl = `${endpointFormatter('fetchAlarmNotificationPreferences')}`;

  const request = () => doPut(apiUrl, preferencesBySite).then(() => doGet(fetchUrl));
  const onSuccess = (response) => {
    const normalized = normalizeAlarmPreferencesResponse(response);
    return setAlarmPreferencesBatch(normalized.length > 0 ? normalized : preferencesBySite);
  };

  dispatch(apiRequest(request, onSuccess, setAlarmPreferencesFail, true, false));
};

/**
 * getAlarmNotificationPreferences
 * Fetch category/device alarm preferences for all sites.
 * @return Promise
 */
export const getAlarmNotificationPreferences = () => (dispatch) =>
  new Promise(() => {
    dispatch(setAlarmPreferencesStart());
    const apiUrl = `${endpointFormatter('fetchAlarmNotificationPreferences')}`;
    const onSuccess = (response) => {
      const normalized = normalizeAlarmPreferencesResponse(response);
      return setAlarmPreferencesBatch(normalized);
    };

    dispatch(apiRequest(() => doGet(apiUrl), onSuccess, setAlarmPreferencesFail));
  });

/**
 * updateNotificationSettings
 * Update notifications settings from toggle button
 * @param {Number} settingId receives setting ID from settings user
 * @param {Object} notificationsData receives data to modify
 */
export const updateNotificationSettings = (settingId, notificationsData) => (dispatch) => {
  dispatch(setNotificationStart());
  const params = {
    settingId,
  };
  const requestData = {
    notificationPreference: notificationsData,
  };
  const apiUrl = `${endpointFormatter('updateNotificationsSettings', params)}`;

  const onSuccess = () => setNotification(notificationsData);

  dispatch(
    apiRequest(() => doPatch(apiUrl, requestData), onSuccess, setNotificationFail, true, false),
  );
};

/**
 * updateUnitMeasurement
 * Update unit measurement from toggle button
 * @param {Number} settingId receives setting ID from settings user
 * @param {Object} unitData  receives data to modify
 */
export const updateUnitMeasurement = (settingId, unitData) => (dispatch) => {
  dispatch(setUnitMeasurementStart());
  const requestData = {
    userSetting: {
      userSettingId: settingId,
      unitMeasurement: unitData,
    },
  };
  const apiUrl = `${endpointFormatter('updateNotificationsSettings')}`;

  const onSuccess = () => setUnitMeasurement(unitData);

  dispatch(
    apiRequest(() => doPatch(apiUrl, requestData), onSuccess, setUnitMeasurementFail, true, false),
  );
};
